import { refsEqual } from './refs';

/**
 * **The ONE definition of "does this actor hold that seat".**
 *
 * A seat (`requesters` / `contributors`) may be held by a person OR by a group,
 * and a group seat is held by that group's MEMBERS (Leon, 2026-07-25: *"any
 * group member in the requester seat ... they are in-role, because they, in
 * fact, have the role"*). Every authority decision in the codebase - the state
 * machine's out-of-role levels, the flow gates' Who, the module field owners,
 * owner reach, and every UI surface that offers or withholds an act - resolves
 * through the functions here.
 *
 * It lives in its own module, and raw `item.requesters.some(...)` /
 * `.includes(...)` predicates are **lint-banned** outside it
 * (`eslint.config.mjs`), because the first cut of this rule taught only the
 * write spine and the item page: the same person was then in-role when they
 * clicked Close and out-of-role when they moved a stage. Uniformly wrong is
 * survivable; inconsistent is not. The ban is what makes "expand groups
 * everywhere" a property of the code rather than of anyone's memory.
 *
 * `speaksFor` is the set of group refs the actor belongs to, resolved by the
 * caller (`memberGroupRefs` server-side, `viewerScopesOf` in reads and UI) and
 * passed in - so these stay pure and do no I/O. Empty is person-identity
 * equality, the pre-2026-07-25 behaviour, which over-flags rather than
 * mis-permits.
 *
 * The rule is `any` member. WHICH member decides (`decision_rule`: lead /
 * quorum / pull-from-pool) is still unmodelled; claiming out of a group pile
 * is done by replacing the group in the seat with yourself.
 */

/** The group refs an actor speaks for: every group they transitively belong to. */
export type SpeaksFor = readonly string[];

/**
 * **Who is acting, and what they speak for** - the pair that must travel
 * together (2026-07-26).
 *
 * `decide` used to take `(actor: string, speaksFor = [])`, and that default was
 * the bug preserved as a signature: a new call site compiled fine and silently
 * asked the person-identity question, so the same person read in-role clicking
 * Close and out-of-role moving a stage. A required value object makes the
 * compiler refuse an actor whose reach nobody resolved.
 *
 * Construct it from a resolved membership set (`memberGroupRefs` server-side,
 * `viewerGroupRefs` / `viewerScopesOf` in reads and UI). `actorWithoutGroups` is the
 * ONE deliberate escape hatch, and it says so at the call site.
 */
export interface Actor {
  /** The acting principal's encoded ref. */
  ref: string;
  /** Every group ref they belong to; a seat held by one of these is theirs. */
  speaksFor: SpeaksFor;
  /**
   * `false` for a narrowed principal without own items (docs/AGENT_ACCESS.md
   * §4 rule 4): a seat held by `ref` itself is then not theirs, only the
   * seats of `speaksFor`. Absent everywhere else. Set only by the narrowing
   * gathers (`actorFor` / `actorFromScopes`, src/read/access-inputs.ts).
   */
  own?: false;
}

export function actorOf(ref: string, speaksFor: SpeaksFor, own = true): Actor {
  return own ? { ref, speaksFor } : { ref, speaksFor, own: false };
}

/**
 * An actor with NO group reach: person-identity equality only. Correct where
 * there is genuinely no membership to resolve - a pure-domain test, a machine
 * principal that holds no seats - and a deliberate under-approximation
 * anywhere else (it over-flags, it never mis-permits). Never reach for it to
 * avoid a lookup.
 */
export function actorWithoutGroups(ref: string): Actor {
  return { ref, speaksFor: [] };
}

/** Does `actor` hold a seat in `seats` - directly, or through a group they
 *  belong to? Takes the `Actor` (below) rather than a ref plus an optional set:
 *  a defaulted `speaksFor` is the footgun this module exists to remove. */
export function holdsSeat(actor: Actor, seats: readonly string[]): boolean {
  return seats.some(
    (r) =>
      (actor.own !== false && refsEqual(actor.ref, r)) ||
      (actor.speaksFor.includes(r) && (actor.own !== false || !refsEqual(actor.ref, r))),
  );
}

/**
 * The refs whose seats `holdsSeat` counts as `actor`'s: their own ref when
 * their direct paths count (`own`), then every group they speak for, once
 * each. It is what the criteria grammar's `self` value stands for when this
 * actor opens a filter (`bindSelf`, src/domain/search-criteria.ts;
 * docs/SEARCH.md "Parties"), so "my work" and the acts the same person is
 * offered on it come from one derivation. The groups carry no role filter,
 * because `holdsSeat` has none: a member at any role, `viewer` included, holds
 * a seat their group holds. For a narrowed agent (docs/AGENT_ACCESS.md §5)
 * `speaksFor` is already the grant's reach.
 */
export function selfRefsOf(actor: Actor): string[] {
  const own = actor.own !== false;
  const refs = own ? [actor.ref] : [];
  for (const r of actor.speaksFor) {
    if (refs.includes(r) || (!own && refsEqual(actor.ref, r))) continue;
    refs.push(r);
  }
  return refs;
}

/** The seat-carrying slice every caller has to hand (a `WorkItemState`, a row,
 *  or a flow member snapshot all satisfy it). */
export interface SeatedItem {
  requesters: readonly string[];
  contributors: readonly string[];
}

export function holdsRequesterSeat(actor: Actor, item: SeatedItem): boolean {
  return holdsSeat(actor, item.requesters);
}

export function holdsContributorSeat(actor: Actor, item: SeatedItem): boolean {
  return holdsSeat(actor, item.contributors);
}

/** Either seat: the "is a party to this contract" test. */
export function isSeatedParty(actor: Actor, item: SeatedItem): boolean {
  return holdsRequesterSeat(actor, item) || holdsContributorSeat(actor, item);
}

/**
 * Personal work: the actor holds the requester seat and nobody ELSE holds the
 * contributor seat (it is empty, or the actor holds it). The contributor seat
 * is strictly optional (Leon, 2026-09-06): a requester who seats nobody knows
 * that nobody else will deliver, so their own Tender, Accept and Close
 * collapse into one gesture exactly as when they seat themselves. An empty
 * contributor seat is never a signal; the last contributor LEAVING is (the
 * Reassign self-walk-away in state-machine.ts carries `warning`).
 */
export function isPersonalWork(actor: Actor, item: SeatedItem): boolean {
  if (!holdsRequesterSeat(actor, item)) return false;
  return item.contributors.length === 0 || holdsContributorSeat(actor, item);
}

/**
 * The ONLY PARTY (docs/CLOSED_AXIS.md "Finishing", 2026-11-03): the actor holds
 * the requester seat and nobody else holds a seat - every requester and every
 * contributor is the actor's own ref. A group seat is never the only party,
 * even for a member: the group's other members are parties too. Whether a seat
 * is OFFERED to someone is the caller's fact (`edgeFactsFor`), since an offer
 * is not a seat.
 */
export function isOnlyParty(actor: Actor, item: SeatedItem): boolean {
  if (actor.own === false || item.requesters.length === 0) return false;
  const own = (r: string) => refsEqual(r, actor.ref);
  // eslint-disable-next-line no-restricted-syntax -- the ONLY-party question is person identity on purpose: a group seat is never the actor alone
  return item.requesters.every(own) && item.contributors.every(own);
}

/**
 * The seat half of a Who evaluation (docs/FLOW_DEFINITION.md §1): which seats
 * this actor holds on this item. The server gathers its inputs in a
 * transaction and the read layer off the pooled db, but the RULE lives here
 * once - the two used to be separate implementations kept in step by a comment
 * saying "MIRRORS ... exactly", which is how a UI ends up offering a move the
 * server then refuses.
 */
export function seatFacts(
  actor: Actor,
  item: SeatedItem,
): { isRequester: boolean; isContributor: boolean } {
  return {
    isRequester: holdsRequesterSeat(actor, item),
    isContributor: holdsContributorSeat(actor, item),
  };
}
